import { css } from '@emotion/css';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Tooltip,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';

import { FeatureId } from '../../config/featureConfig';
import { useLicenseEntitlement } from '../../hooks/useLicenseEntitlement';
import { UpgradeModal } from '../licensing';

export interface NavItem {
  value: string;
  label: string;
  icon: React.ReactNode;
  /** HA-136: Feature ID for tier-gating this nav item */
  featureId?: FeatureId;
}

interface MobileNavDrawerProps {
  open: boolean;
  onClose: () => void;
  navItems: NavItem[];
  activeNav: string;
  onNavChange: (value: string) => void;
  onLogout: () => void;
  title?: string;
}

/**
 * Mobile navigation drawer component
 * Provides a slide-out navigation menu for mobile devices
 */
const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({
  open,
  onClose,
  navItems,
  activeNav,
  onNavChange,
  onLogout,
  title = 'Practice Center',
}) => {
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === 'dark';
  const { checkFeature } = useLicenseEntitlement();

  // HA-136: State for upgrade modal
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [lockedFeatureId, setLockedFeatureId] = useState<FeatureId | undefined>();

  const handleNavClick = (item: NavItem) => {
    // HA-136: Check if feature is locked
    if (item.featureId) {
      const featureCheck = checkFeature(item.featureId);

      if (!featureCheck.isAvailable) {
        setLockedFeatureId(item.featureId);
        setUpgradeModalOpen(true);

        return;
      }
    }
    onNavChange(item.value);
    onClose();
  };

  const styles = {
    drawerPaper: css({
      width: '85%',
      maxWidth: 320,
      backgroundColor: isDarkTheme ? theme.palette.background.paper : '#0f3658',
      color: '#ffffff',
    }),
    header: css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(2, 2, 2, 3),
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    }),
    title: css({
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: isDarkTheme ? theme.palette.primary.light : '#8ff0d3',
    }),
    closeButton: css({
      color: 'rgba(255, 255, 255, 0.8)',
    }),
    content: css({
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(2),
    }),
    navList: css({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: 0,
    }),
    navItem: css({
      borderRadius: Number(theme.shape.borderRadius) * 1.5,
      color: 'rgba(255, 255, 255, 0.8)',
      minHeight: 48,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    }),
    navItemActive: css({
      borderRadius: Number(theme.shape.borderRadius) * 1.5,
      backgroundColor: 'rgba(143, 240, 211, 0.18)',
      color: '#ffffff',
      borderLeft: '4px solid #8ff0d3',
      minHeight: 48,
      '&:hover': {
        backgroundColor: 'rgba(143, 240, 211, 0.28)',
      },
      '& .MuiListItemIcon-root': {
        color: '#8ff0d3',
      },
    }),
    navIcon: css({
      color: 'inherit',
      minWidth: 40,
    }),
    logoutButton: css({
      marginTop: 'auto',
      color: 'rgba(255, 255, 255, 0.85)',
      borderRadius: Number(theme.shape.borderRadius) * 1.5,
      minHeight: 48,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    }),
    // HA-136: Locked nav item style
    navItemLocked: css({
      borderRadius: Number(theme.shape.borderRadius) * 1.5,
      color: 'rgba(255, 255, 255, 0.5)',
      minHeight: 48,
      opacity: 0.7,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      },
    }),
    tierBadge: css({
      backgroundColor: 'rgba(255, 193, 7, 0.2)',
      color: '#ffc107',
      fontSize: '0.65rem',
      height: 18,
      '& .MuiChip-icon': {
        color: '#ffc107',
        fontSize: 12,
        marginLeft: 4,
      },
    }),
  };

  // HA-136: Helper to check if item is locked
  const isItemLocked = (item: NavItem): boolean => {
    if (!item.featureId) return false;
    const featureCheck = checkFeature(item.featureId);

    return !featureCheck.isAvailable;
  };

  // HA-136: Get required tier name for locked item
  const getRequiredTierName = (item: NavItem): string => {
    if (!item.featureId) return '';
    const featureCheck = checkFeature(item.featureId);

    return featureCheck.requiredTierName || '';
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          className: styles.drawerPaper,
        },
      }}
    >
      <Box className={styles.header}>
        <Typography variant="h6" className={styles.title}>
          {title}
        </Typography>
        <IconButton onClick={onClose} className={styles.closeButton} aria-label="close menu">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className={styles.content}>
        <List className={styles.navList}>
          {navItems.map((item) => {
            const locked = isItemLocked(item);
            const requiredTier = getRequiredTierName(item);

            return (
              <Tooltip
                key={item.value}
                title={locked ? `Requires ${requiredTier} tier` : ''}
                placement="right"
              >
                <ListItemButton
                  onClick={() => handleNavClick(item)}
                  className={
                    locked
                      ? styles.navItemLocked
                      : item.value === activeNav
                        ? styles.navItemActive
                        : styles.navItem
                  }
                >
                  <ListItemIcon className={styles.navIcon}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                  {/* HA-136: Show tier badge for locked items */}
                  {locked && (
                    <Chip
                      icon={<LockIcon />}
                      label={requiredTier}
                      size="small"
                      className={styles.tierBadge}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>

        <ListItemButton onClick={onLogout} className={styles.logoutButton}>
          <ListItemIcon className={styles.navIcon}>
            <LogoutRoundedIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </Box>
      {/* HA-136: Upgrade modal for locked features */}
      <UpgradeModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        featureId={lockedFeatureId}
      />
    </Drawer>
  );
};

export default MobileNavDrawer;
